// Utilitários de exibição de dados de usuário (perfil, contatos)

// Iniciais para o avatar: primeira letra do primeiro e do último nome
export const getInitials = (name?: string): string => {
  const words = (name ?? "")
    .trim()
    .split(/\s+/)
    .filter((word) => /^[A-Za-z0-9À-ÿ]/.test(word));

  if (words.length === 0) return "?";

  const first = Array.from(words[0])[0];
  const last = words.length > 1 ? Array.from(words[words.length - 1])[0] : "";
  return `${first}${last}`.toUpperCase();
};

export const getFirstName = (name?: string): string =>
  (name ?? "").trim().split(/\s+/)[0] || "";

// Cor fixa por pessoa (derivada do código), para o mesmo contato sempre ter a
// mesma cor no avatar
const AVATAR_COLORS = [
  "#C05A21",
  "#6C42D6",
  "#2E7D32",
  "#1565C0",
  "#AD1457",
  "#00838F",
  "#EF6C00",
  "#5D4037",
];

// hash FNV-1a com a mistura final do MurmurHash3: códigos parecidos
// (1111, 2222...) caem em cores diferentes
export const getAvatarColor = (seed: number | string = 0): string => {
  const text = String(seed);
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  return AVATAR_COLORS[(hash >>> 0) % AVATAR_COLORS.length];
};

// O campo `avatar` ainda guarda o texto "url" em todas as contas: só vale
// como foto quando for um endereço de imagem de verdade
export const isImageUrl = (value?: string): boolean =>
  !!value && /^https:\/\//.test(value);

export const onlyDigits = (value?: string): string =>
  (value ?? "").replace(/\D/g, "");

// (19) 99999-9999 ou (19) 3333-4444, formatando enquanto a pessoa digita
export const formatCellphone = (value?: string): string => {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return `(${digits}`;

  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);
  const split = digits.length === 11 ? 5 : 4;

  if (rest.length <= split) return `(${ddd}) ${rest}`;
  return `(${ddd}) ${rest.slice(0, split)}-${rest.slice(split)}`;
};

export const isValidCellphone = (value?: string): boolean =>
  /^\d{10,11}$/.test(onlyDigits(value));

export const formatCoins = (value?: number): string =>
  (value ?? 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 });

// Copia texto para a área de transferência. O fallback cobre navegadores
// antigos e páginas fora de HTTPS, onde `navigator.clipboard` não existe.
export const copyText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // segue para o fallback
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
};

export const canShare = (): boolean =>
  typeof navigator !== "undefined" && typeof navigator.share === "function";

// Compartilha o código pelo menu nativo do celular (WhatsApp etc.)
export const shareUserCode = async (code: number): Promise<void> => {
  try {
    await navigator.share({
      title: "Mutattio Coins",
      text: `Meu código no Mutattio Coins é ${code}. Use-o para me enviar moedas ou me adicionar aos contatos.`,
    });
  } catch {
    // fechar o menu de compartilhamento não é erro
  }
};
